import { useState, useEffect, useRef } from "react";
import { Check, CalendarClock, ChevronDown, ArrowRight } from "lucide-react";
import Cal, { getCalApi } from "@calcom/embed-react";
import { api } from "@/lib/api";

const RED = "#C92F2F";
const RED_HOVER = "#A82525";

const UTM_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"];
const UTM_STORE = "goose_partner_utms";

const US_STATES = ["AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC"];

const FIELDS = [
  { k: "first_name", label: "First name", ph: "Dana", col: true },
  { k: "last_name", label: "Last name", ph: "Reed", col: true },
  { k: "email", label: "Email", ph: "you@company.com", col: true, type: "email" },
  { k: "phone", label: "Phone", ph: "(415) 555-1234", col: true, type: "tel" },
  { k: "organization", label: "Company / Organization", ph: "Rooftop Collective" },
  { k: "city", label: "City", ph: "Austin", col: true },
  { k: "state", label: "State", ph: "TX", col: true, type: "select" },
  { k: "event_url", label: "Event website, Eventbrite page, or social profile", ph: "eventbrite.com/o/your-events" },
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const uid = () => "tok-" + Math.random().toString(36).slice(2) + Date.now().toString(36);

const CARD = "rounded-[24px] bg-white p-6 sm:p-9 shadow-[0_24px_60px_rgba(0,0,0,0.12)] ring-1 ring-black/5";
const inputCls =
  "w-full rounded-[10px] border border-[#E4E6EA] bg-white px-4 py-3 text-[15px] text-[#111111] " +
  "placeholder:text-[#B6BBC2] outline-none transition-colors focus:border-[#C92F2F] " +
  "focus:ring-2 focus:ring-[#C92F2F]/25";
const labelCls = "mb-1.5 block text-[13px] font-semibold text-[#111111]";

function captureUtms() {
  let stored = {};
  try { stored = JSON.parse(sessionStorage.getItem(UTM_STORE) || "{}"); } catch { stored = {}; }
  const params = new URLSearchParams(window.location.search);
  let changed = false;
  UTM_KEYS.forEach((k) => {
    const v = params.get(k);
    if (v && !stored[k]) { stored[k] = v; changed = true; }
  });
  if (changed) {
    try { sessionStorage.setItem(UTM_STORE, JSON.stringify(stored)); } catch { /* ignore */ }
  }
  return stored;
}

function CalEmbed({ calLink, name, email }) {
  useEffect(() => {
    let active = true;
    (async () => {
      const cal = await getCalApi({ namespace: "goose" });
      if (active) cal("ui", { hideEventTypeDetails: false, layout: "month_view" });
    })();
    return () => { active = false; };
  }, []);
  return (
    <Cal
      namespace="goose"
      calLink={calLink}
      style={{ width: "100%", height: "100%", overflow: "scroll" }}
      config={{ layout: "month_view", name, email }}
    />
  );
}

export function PartnerLeadForm() {
  const [form, setForm] = useState(
    Object.fromEntries([...FIELDS.map((f) => f.k), "event_description"].map((k) => [k, ""]))
  );
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [scheduled, setScheduled] = useState(null);
  const [cal, setCal] = useState(null);
  const tokenRef = useRef(uid());

  useEffect(() => { captureUtms(); }, []);
  useEffect(() => {
    if (scheduled) {
      api.get("/public/calcom-config").then((r) => setCal(r.data)).catch(() => setCal({ configured: false }));
    }
  }, [scheduled]);

  const set = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const er = {};
    [...FIELDS.map((f) => f.k), "event_description"].forEach((k) => {
      if (!String(form[k] || "").trim()) er[k] = "Required";
    });
    if (form.email && !EMAIL_RE.test(form.email.trim())) er.email = "Enter a valid email";
    if (form.phone && form.phone.replace(/[^\d]/g, "").length < 7) er.phone = "Enter a valid phone number";
    setErrors(er);
    return Object.keys(er).length === 0;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    if (!validate()) return;
    setSubmitting(true);
    try {
      const utms = captureUtms();
      const payload = {
        ...Object.fromEntries(Object.entries(form).map(([k, v]) => [k, String(v).trim()])),
        ...utms,
        client_token: tokenRef.current,
        page_url: window.location.href,
      };
      const { data } = await api.post("/public/partner-prospects", payload);
      setScheduled(data.prospect);
    } catch (err) {
      const status = err?.response?.status;
      setErrors({ _form: status === 429
        ? "You've submitted a few times already. Please try again in a little while."
        : "Something went wrong submitting your information. Please try again." });
    } finally {
      setSubmitting(false);
    }
  };

  if (scheduled) {
    const calReady = cal?.configured && cal?.username && cal?.eventSlug;
    const calLink = calReady ? `${cal.username}/${cal.eventSlug}` : null;
    const fullName = `${scheduled.first_name} ${scheduled.last_name}`.trim();
    return (
      <div className={CARD} data-testid="partner-scheduling-state">
        <div className="text-center">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full" style={{ backgroundColor: "#FDECED", color: RED }}>
            <Check className="h-6 w-6" />
          </div>
          <h2 className="mt-4 font-display text-[1.75rem] font-extrabold leading-[1.1] tracking-[-0.03em] text-[#111111]">
            Thanks. Let’s talk.
          </h2>
          <p className="mx-auto mt-3 max-w-[460px] text-[15px] leading-[1.5] text-[#3F4650]">
            Pick a time that works for you and we’ll show you how Goose fits the events you already produce.
          </p>
        </div>

        {cal === null ? (
          <div className="mt-6 flex h-[200px] items-center justify-center text-[14px] text-[#89909A]">Loading scheduler…</div>
        ) : calReady ? (
          <div data-testid="cal-embed" className="mt-6 h-[600px] w-full overflow-hidden rounded-[12px] border border-[#EEEEEE] lg:h-[660px]">
            <CalEmbed calLink={calLink} name={fullName} email={scheduled.email} />
          </div>
        ) : (
          <div data-testid="cal-pending" className="mx-auto mt-6 max-w-[440px] rounded-[12px] border border-[#EEEEEE] bg-[#F9FAFB] px-6 py-7 text-center">
            <CalendarClock className="mx-auto h-6 w-6" style={{ color: RED }} />
            <p className="mt-3 text-[15px] font-semibold text-[#111111]">Your scheduler is being connected</p>
            <p className="mt-2 text-[14px] leading-[1.55] text-[#3F4650]">
              Your details are saved. A booking calendar will appear here shortly — in the meantime, we’ll reach out to find a time.
            </p>
          </div>
        )}
      </div>
    );
  }

  const renderField = (f) => (
    <div key={f.k} className={f.col ? "" : "sm:col-span-2"}>
      <label htmlFor={f.k} className={labelCls}>
        {f.label} <span style={{ color: RED }}>*</span>
      </label>
      {f.type === "select" ? (
        <div className="relative">
          <select
            id={f.k}
            data-testid={`lead-${f.k.replace(/_/g, "-")}`}
            value={form[f.k]}
            onChange={set(f.k)}
            className={`${inputCls} appearance-none pr-10 ${form[f.k] ? "" : "text-[#B6BBC2]"}`}
            aria-invalid={!!errors[f.k]}
          >
            <option value="" disabled>{f.ph}</option>
            {US_STATES.map((s) => <option key={s} value={s} className="text-[#111111]">{s}</option>)}
          </select>
          <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#6B7280]" />
        </div>
      ) : (
        <input
          id={f.k}
          data-testid={`lead-${f.k.replace(/_/g, "-")}`}
          type={f.type || "text"}
          value={form[f.k]}
          onChange={set(f.k)}
          placeholder={f.ph}
          className={inputCls}
          aria-invalid={!!errors[f.k]}
        />
      )}
      {errors[f.k] && <p className="mt-1 text-[12px] font-medium" style={{ color: RED }}>{errors[f.k]}</p>}
    </div>
  );

  return (
    <div className={CARD}>
      <h2 className="font-display text-[1.75rem] sm:text-[2rem] font-extrabold tracking-[-0.03em] text-[#111111]">
        Tell us about your events.
      </h2>
      <p className="mt-1 text-[15px] text-[#89909A]">It takes about a minute.</p>

      <form data-testid="partner-lead-form" onSubmit={submit} noValidate className="mt-6">
        <div className="grid grid-cols-1 gap-x-5 gap-y-5 sm:grid-cols-2">
          {FIELDS.map(renderField)}
          <div className="sm:col-span-2">
            <label htmlFor="event_description" className={labelCls}>
              Tell us about the events you produce <span style={{ color: RED }}>*</span>
            </label>
            <textarea
              id="event_description"
              data-testid="lead-event-description"
              value={form.event_description}
              onChange={set("event_description")}
              rows={4}
              placeholder="What kinds of events do you run, how often, and roughly how many guests attend?"
              className={`${inputCls} resize-y`}
              aria-invalid={!!errors.event_description}
            />
            {errors.event_description && <p className="mt-1 text-[12px] font-medium" style={{ color: RED }}>{errors.event_description}</p>}
          </div>
        </div>

        {errors._form && (
          <p data-testid="lead-form-error" className="mt-5 text-[14px] font-medium" style={{ color: RED }}>{errors._form}</p>
        )}

        <button
          type="submit"
          data-testid="lead-submit-btn"
          disabled={submitting}
          className="group mt-7 inline-flex w-full items-center justify-center gap-2 rounded-[12px] px-7 py-4 text-[16px] font-bold text-white transition-colors duration-200 disabled:opacity-60"
          style={{ backgroundColor: RED }}
          onMouseEnter={(e) => { if (!submitting) e.currentTarget.style.backgroundColor = RED_HOVER; }}
          onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = RED)}
        >
          {submitting ? "Submitting…" : "Continue to scheduling"}
          <ArrowRight className="h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
        </button>
        <p className="mt-3 text-center text-[13px] text-[#89909A]">
          Your information will be saved before you continue to scheduling.
        </p>
      </form>
    </div>
  );
}
